import type React from "react";
import ButtonGroup from "../../../../components/Forms/ButtonGroup";
import NewLabelWrapper from "../../../../components/Forms/NewLabelWrapper";
import { MAX_SERIAL_LENGTH, type PadConfig } from "./padSerialNumber";

interface Props {
  padConfig: PadConfig;
  setPadConfig: (config: PadConfig) => void;
}

// Composite control: a single "Serial number" heading + description, then
// three inline-labelled controls on the same row — Verify Length, Pad Short,
// and the shared length input. The two ButtonGroups and the numeric input
// share the same h-[38px] so they line up.
export default function PadControls(props: Props): React.ReactElement {
  const { padConfig, setPadConfig } = props;
  const lengthInputEnabled = padConfig.enabled || padConfig.verifyLength;
  return (
    <div className="my-4">
      <NewLabelWrapper
        label="Serial number"
        description="Verify that each row's serial is the expected length, and optionally pad numeric serials with leading zeros (in case a spreadsheet tool stripped them before the CSV was saved)."
        size="md"
      >
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 whitespace-nowrap">
              Verify Length:
            </span>
            <ButtonGroup
              dataTestId="verify-length-enabled"
              label={{ size: "sm", margin: 0 }}
              value={padConfig.verifyLength}
              options={[
                { label: "On", value: true },
                { label: "Off", value: false },
              ]}
              onChange={(value) =>
                setPadConfig({ ...padConfig, verifyLength: value as boolean })
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 whitespace-nowrap">
              Pad Short:
            </span>
            <ButtonGroup
              dataTestId="pad-enabled"
              label={{ size: "sm", margin: 0 }}
              value={padConfig.enabled}
              options={[
                { label: "On", value: true },
                { label: "Off", value: false },
              ]}
              onChange={(value) =>
                setPadConfig({ ...padConfig, enabled: value as boolean })
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <label
              htmlFor="pad-length"
              className="text-sm text-gray-700 whitespace-nowrap"
            >
              Length:
            </label>
            <input
              id="pad-length"
              type="number"
              data-testid="pad-length"
              aria-label="Serial number length"
              min={0}
              max={MAX_SERIAL_LENGTH}
              value={padConfig.length}
              disabled={!lengthInputEnabled}
              onChange={(e) => {
                const next = Number(e.target.value);
                // Number("") and Number("abc") give NaN; clamp to 0 so the
                // field can be cleared during editing. padSerialNumber
                // treats 0 as off. Clamp the upper bound too: HTML max=
                // is just a hint, and a length > MAX_SERIAL_LENGTH would
                // produce values the backend rejects with 422.
                const clamped = Number.isFinite(next)
                  ? Math.min(Math.max(next, 0), MAX_SERIAL_LENGTH)
                  : 0;
                setPadConfig({ ...padConfig, length: clamped });
              }}
              className="block w-20 h-[38px] rounded-lg border-0 px-3 sm:text-sm sm:leading-6 ring-1 text-gray-900 shadow-sm ring-gray-300 placeholder:text-gray-400 focus:ring-brand-light-600 disabled:opacity-50"
            />
          </div>
        </div>
      </NewLabelWrapper>
    </div>
  );
}
